/**
 * Dictation in the composer (R57 to R62; `docs/design/ai-buddy-dictation-2026-10-13/`): the states a
 * dictation passes through, where its text lands in the field, and what the composer says about it.
 * Pure: the microphone, the recorder and the request are `lib/useDictation.ts`'s.
 *
 *   idle ──start──▶ requesting ──granted──▶ recording ──stop──▶ transcribing ──heard──▶ idle (inserted)
 *                       │                      │  ├─stop, nothing heard─▶ idle (empty)    ├─silent──▶ idle (empty)
 *                       └─micRefused─▶ idle    │  └─lost (the track ended)─▶ idle (noDevice)
 *                                              └──────cancel──────────────▶ idle         ├─failed──▶ idle (failed) ─retry─▶ transcribing
 *                                                                                         └─refused─▶ idle (off)
 *   any phase but idle ──refused (the offer went off: a full window, Buddy switched off)──▶ idle (off)
 *
 * Every event that does not belong to the phase it arrives in is ignored: a late answer, a second
 * stop, a retry with nothing kept to send again.
 */

import type { AiRefusal } from '~/entities/ai-conversation';

export type DictationPhase = 'idle' | 'requesting' | 'recording' | 'transcribing';

/**
 * Why a dictation cannot go on, said with its own words rather than as a failure: a usage window is
 * full, Buddy is switched off for the trainer, or one dictation too many at once (R60).
 */
export type DictationOffReason = 'limit' | 'unavailable' | 'busy';

/** What the last dictation came to, said in the tray until the trainer moves on. */
export type DictationOutcome =
  /** The text is in the field. `auto`: the recording reached its longest length and stopped itself. */
  | { readonly kind: 'inserted'; readonly auto: boolean; readonly words: number }
  /** Nothing heard: an empty transcript, or a clip too short or too quiet to send at all. */
  | { readonly kind: 'empty' }
  /** No text. `retryable`: the same recording is kept and „Ponów" sends it again. */
  | { readonly kind: 'failed'; readonly retryable: boolean }
  | { readonly kind: 'denied' }
  | { readonly kind: 'noDevice' }
  | { readonly kind: 'cancelled' }
  /** Refused for a reason that is not the recording's: no retry, the words name the reason. */
  | { readonly kind: 'off'; readonly reason: DictationOffReason };

export interface DictationState {
  readonly phase: DictationPhase;
  readonly outcome: DictationOutcome | null;
  /** Whether the recording now being transcribed stopped itself at the longest length. */
  readonly auto: boolean;
}

export type DictationEvent =
  | { readonly type: 'start' }
  | { readonly type: 'granted' }
  | { readonly type: 'micRefused'; readonly why: 'denied' | 'noDevice' }
  /** The recording ended; `heard` is `clipWorthSending`'s verdict, so a silent clip is never sent. */
  | { readonly type: 'stop'; readonly auto: boolean; readonly heard: boolean }
  | { readonly type: 'heard'; readonly words: number }
  | { readonly type: 'silent' }
  | { readonly type: 'failed'; readonly retryable: boolean }
  | { readonly type: 'retry' }
  | { readonly type: 'cancel' }
  /** The microphone's track ended by itself (unplugged, permission revoked) with nothing worth sending. */
  | { readonly type: 'lost' }
  /** The server, or the offer, refused for a reason of its own; nothing reaches the field. */
  | { readonly type: 'refused'; readonly reason: DictationOffReason }
  /** The tray goes back to its own sentence: the trainer typed, or the moment passed. */
  | { readonly type: 'settle' };

export const DICTATION_IDLE: DictationState = { phase: 'idle', outcome: null, auto: false };

const settled = (outcome: DictationOutcome): DictationState => ({ phase: 'idle', outcome, auto: false });

export function dictationReducer(state: DictationState, event: DictationEvent): DictationState {
  switch (event.type) {
    case 'start':
      return state.phase === 'idle' ? { phase: 'requesting', outcome: null, auto: false } : state;
    case 'granted':
      return state.phase === 'requesting' ? { ...state, phase: 'recording' } : state;
    case 'micRefused':
      return state.phase === 'requesting' ? settled({ kind: event.why }) : state;
    case 'stop':
      if (state.phase !== 'recording') return state;
      return event.heard ? { phase: 'transcribing', outcome: null, auto: event.auto } : settled({ kind: 'empty' });
    case 'heard':
      return state.phase === 'transcribing' ? settled({ kind: 'inserted', auto: state.auto, words: event.words }) : state;
    case 'silent':
      return state.phase === 'transcribing' ? settled({ kind: 'empty' }) : state;
    case 'failed':
      return state.phase === 'transcribing' ? settled({ kind: 'failed', retryable: event.retryable }) : state;
    case 'retry':
      return state.phase === 'idle' && state.outcome?.kind === 'failed' && state.outcome.retryable
        ? { phase: 'transcribing', outcome: null, auto: false }
        : state;
    case 'cancel':
      return state.phase === 'idle' ? state : settled({ kind: 'cancelled' });
    case 'lost':
      return state.phase === 'recording' ? settled({ kind: 'noDevice' }) : state;
    case 'refused':
      return state.phase === 'idle' ? state : settled({ kind: 'off', reason: event.reason });
    case 'settle':
      return state.phase === 'idle' && state.outcome !== null ? DICTATION_IDLE : state;
  }
}

/** Whether a dictation is under way: the lane stands over the composer's own controls. */
export function isDictating(phase: DictationPhase): boolean {
  return phase !== 'idle';
}

/** Shorter than this, a clip is a slip of the shortcut, not speech (board notes, „Nothing heard"). */
export const SHORTEST_CLIP_MS = 500;

/** A level (0 to 1) under this is drawn as a dot and is not speech. */
export const SPEECH_LEVEL = 0.12;

/** Whether a finished recording is worth sending: long enough, and loud enough at least once. */
export function clipWorthSending(durationMs: number, loudestLevel: number): boolean {
  return durationMs >= SHORTEST_CLIP_MS && loudestLevel >= SPEECH_LEVEL;
}

/**
 * The loudness of one analyser frame as the waveform draws it: RMS → dBFS, −55 dB to −10 dB onto
 * 0 to 1, eased by 0.7 so quiet speech still shows (board notes, „Waveform").
 */
export function levelOfFrame(samples: ArrayLike<number>): number {
  if (samples.length === 0) return 0;
  let sum = 0;
  for (let index = 0; index < samples.length; index += 1) {
    const sample = samples[index] ?? 0;
    sum += sample * sample;
  }
  const decibels = 20 * Math.log10(Math.sqrt(sum / samples.length) + 1e-9);
  return Math.min(1, Math.max(0, (decibels + 55) / 45)) ** 0.7;
}

/**
 * The container a browser records in, by preference: WebM with Opus, then MP4 (Safari), then Ogg.
 * `null` when it names none of them supported; the recorder then picks its own default.
 */
const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'] as const;

export function recordingType(isSupported: (type: string) => boolean): string | null {
  return RECORDING_TYPES.find((type) => isSupported(type)) ?? null;
}

/** The last stretch of a recording counts down, in whole seconds left; `null` before it. */
export const COUNTDOWN_SECONDS = 10;

export function secondsLeft(elapsedSeconds: number, maxSeconds: number): number | null {
  const left = Math.max(0, maxSeconds - elapsedSeconds);
  return left <= COUNTDOWN_SECONDS ? left : null;
}

/** `0:07`, `1:00`: the timer by the waveform. */
export function clockOf(seconds: number): string {
  const whole = Math.max(0, Math.floor(seconds));
  return `${String(Math.floor(whole / 60))}:${String(whole % 60).padStart(2, '0')}`;
}

export function wordCount(text: string): number {
  const trimmed = text.trim();
  return trimmed === '' ? 0 : trimmed.split(/\s+/).length;
}

/** Where the trainer's caret stood when the dictation began; `null` when the field did not hold focus. */
export interface FieldCaret {
  readonly start: number;
  readonly end: number;
}

/** What to write into the field: `insert` replaces the (always empty) range `start`–`end`. */
export interface DictationInsertion {
  readonly start: number;
  readonly end: number;
  readonly insert: string;
}

const isSpace = (character: string | undefined) => character !== undefined && /\s/.test(character);

/**
 * Where a transcript lands (R58): at the caret, or with no caret after what is there. Nothing the
 * trainer wrote is replaced — a selection collapses to its end first — and the words are kept apart
 * from their neighbours by one space, never two, with none at the start of an empty field.
 */
export function dictationInsertion(value: string, caret: FieldCaret | null, transcript: string): DictationInsertion {
  const text = transcript.trim();
  const at = caret === null ? value.length : Math.min(Math.max(caret.start, caret.end, 0), value.length);
  const before = value[at - 1];
  const after = value[at];
  const lead = at > 0 && !isSpace(before) ? ' ' : '';
  const trail = after !== undefined && !isSpace(after) ? ' ' : '';
  return { start: at, end: at, insert: `${lead}${text}${trail}` };
}

/** Words to say: a `web:` key and the values it interpolates. */
export interface DictationCopy {
  readonly key: string;
  readonly values?: Readonly<Record<string, number | string>>;
}

/** One line of the tray: the words, their tone, and the act beside them. */
export interface DictationLine extends DictationCopy {
  /** `strong`: the trainer's attention is wanted now (the prompt, the countdown). */
  readonly tone: 'plain' | 'strong';
  /** The mark before an outcome's words; only a failure is coloured, and only the mark. */
  readonly glyph: { readonly kind: 'hear' | 'alert'; readonly danger: boolean } | null;
  readonly action: 'undo' | 'again' | 'retry' | 'tryAgain' | null;
}

const DICTATION = 'web:aiPlan.dictation';
const LINE = `${DICTATION}.tray`;

/**
 * Why dictation does not go on, in words (R57): the same reason as sending, naming when Buddy is
 * back where the server said (`resetWhen`, already worded: „o 14:20").
 */
export function dictationOffCopy(reason: DictationOffReason, resetWhen: string | null): DictationCopy {
  switch (reason) {
    case 'unavailable':
      return { key: `${DICTATION}.offUnavailable` };
    case 'busy':
      return { key: `${DICTATION}.offBusy` };
    case 'limit':
      return resetWhen === null ? { key: `${DICTATION}.offLimitLater` } : { key: `${DICTATION}.offLimit`, values: { when: resetWhen } };
  }
}

/**
 * What the tray says about the dictation, or `null` when it says its own sentence (the sheet
 * offer): before a dictation, after it settled, and after a cancel, which the live region says.
 */
export function dictationLine(state: DictationState, left: number | null, resetWhen: string | null): DictationLine | null {
  switch (state.phase) {
    case 'requesting':
      return { key: `${LINE}.asking`, tone: 'strong', glyph: null, action: null };
    case 'recording':
      return left === null
        ? { key: `${LINE}.listening`, tone: 'plain', glyph: null, action: null }
        : { key: `${LINE}.lastSeconds`, values: { count: left }, tone: 'strong', glyph: null, action: null };
    case 'transcribing':
      return { key: `${LINE}.transcribing`, tone: 'plain', glyph: null, action: null };
    case 'idle':
      break;
  }
  switch (state.outcome?.kind) {
    case undefined:
    case 'cancelled':
      return null;
    case 'inserted':
      return { key: state.outcome.auto ? `${LINE}.autoStopped` : `${LINE}.inserted`, tone: 'plain', glyph: null, action: 'undo' };
    case 'empty':
      return { key: `${LINE}.nothingHeard`, tone: 'plain', glyph: { kind: 'hear', danger: false }, action: 'again' };
    case 'failed':
      return { key: `${LINE}.failed`, tone: 'plain', glyph: { kind: 'alert', danger: true }, action: state.outcome.retryable ? 'retry' : null };
    case 'denied':
      return { key: `${LINE}.denied`, tone: 'plain', glyph: { kind: 'alert', danger: false }, action: 'tryAgain' };
    case 'noDevice':
      return { key: `${LINE}.noDevice`, tone: 'plain', glyph: { kind: 'alert', danger: false }, action: 'tryAgain' };
    case 'off':
      return { ...dictationOffCopy(state.outcome.reason, resetWhen), tone: 'plain', glyph: { kind: 'alert', danger: false }, action: null };
  }
}

const SAY = `${DICTATION}.say`;

/**
 * What the one polite live region says (board notes, „Accessibility"): the start, the last ten
 * seconds once, the transcribing, the result and every refusal. Never the clock each second.
 */
export function dictationAnnouncement(state: DictationState, left: number | null, resetWhen: string | null): DictationCopy | null {
  switch (state.phase) {
    case 'requesting':
      return { key: `${SAY}.asking` };
    case 'recording':
      return left === null ? { key: `${SAY}.recording` } : { key: `${SAY}.lastSeconds`, values: { count: COUNTDOWN_SECONDS } };
    case 'transcribing':
      return { key: `${SAY}.transcribing` };
    case 'idle':
      break;
  }
  switch (state.outcome?.kind) {
    case undefined:
      return null;
    case 'inserted':
      return { key: `${SAY}.inserted`, values: { count: state.outcome.words } };
    case 'cancelled':
      return { key: `${SAY}.cancelled` };
    case 'empty':
    case 'failed':
    case 'denied':
    case 'noDevice':
    case 'off': {
      const line = dictationLine(state, null, resetWhen);
      return line === null ? null : { key: line.key, ...(line.values === undefined ? {} : { values: line.values }) };
    }
  }
}

/**
 * What a refused transcription comes to (R59): a full window, Buddy switched off or one dictation
 * too many name their reason and offer no retry; only a failure of the provider or of the network is
 * worth the same recording again. Anything else about the recording itself is not.
 */
export function transcriptionRefused(refusal: AiRefusal): DictationEvent {
  switch (refusal.kind) {
    case 'limit':
    case 'unavailable':
    case 'busy':
      return { type: 'refused', reason: refusal.kind };
    case 'failed':
      return { type: 'failed', retryable: true };
    case 'dictation':
      return { type: 'failed', retryable: refusal.reason === 'transcriptionFailed' };
    default:
      return { type: 'failed', retryable: false };
  }
}

/** Whether an outcome gives the tray back by itself after a moment: it asks nothing of the trainer. */
export function settlesByItself(outcome: DictationOutcome | null): boolean {
  return outcome?.kind === 'inserted' || outcome?.kind === 'cancelled' || outcome?.kind === 'off';
}

/** Where the composer's mic lives (R57): the hero and the dock, never the builder's panel or a rejection's reason. */
export function dictationHere(layout: 'hero' | 'dock' | 'panel', rejecting: boolean): boolean {
  return layout !== 'panel' && !rejecting;
}

/** The round action: send, stop while Buddy works, ✓ while the mic is asked for and while recording, a spinner while transcribing. */
export type DictationActionKind = 'send' | 'stop' | 'waitingMic' | 'insert' | 'transcribing';

export function dictationAction(phase: DictationPhase, working: boolean): DictationActionKind {
  switch (phase) {
    case 'requesting':
      return 'waitingMic';
    case 'recording':
      return 'insert';
    case 'transcribing':
      return 'transcribing';
    case 'idle':
      return working ? 'stop' : 'send';
  }
}

/**
 * Enter while a dictation runs: on ✓ or in the field it stops and inserts (and never sends); on any
 * other button — × above all — the browser activates that button, so Enter on × cancels.
 */
export function dictationEnter(phase: DictationPhase, on: 'action' | 'field' | 'otherButton'): 'stop' | 'swallow' | 'native' {
  if (on === 'otherButton') return 'native';
  return phase === 'recording' ? 'stop' : 'swallow';
}

/**
 * Where focus goes when the dictation changes state: to the round action while it asks, records and
 * transcribes (the controls it replaces go inert, and „Ponów" leaves with its line), back to where it
 * began on a cancel, and to the field once it has an answer. `null`: leave focus where it is (the
 * text that landed puts the caret in the field itself).
 */
export function dictationFocus(state: DictationState, origin: 'field' | 'mic'): 'action' | 'mic' | 'field' | null {
  if (state.phase !== 'idle') return state.phase === 'recording' ? null : 'action';
  switch (state.outcome?.kind) {
    case undefined:
    case 'inserted':
      return null;
    case 'cancelled':
      return origin;
    default:
      return 'field';
  }
}

/** What stands over the hero's read-only field: a mark where the text will land, then „przepisuję…" there. */
export function dictationMirror(layout: 'hero' | 'dock' | 'panel', phase: DictationPhase, hasText: boolean): 'marker' | 'ghost' | null {
  if (layout !== 'hero') return null;
  if (phase === 'transcribing') return 'ghost';
  return (phase === 'requesting' || phase === 'recording') && hasText ? 'marker' : null;
}

/** The dock has no field to carry „przepisuję…", so its lane says it in the timer's place. */
export function laneSaysTranscribing(layout: 'hero' | 'dock', phase: DictationPhase): boolean {
  return layout === 'dock' && phase === 'transcribing';
}

/** The waveform: flat dots while the browser asks, the voice while recording, falling to dots after. */
export type WaveMode = 'flat' | 'live' | 'collapse';

export function waveMode(phase: Exclude<DictationPhase, 'idle'>): WaveMode {
  return phase === 'requesting' ? 'flat' : phase === 'recording' ? 'live' : 'collapse';
}
